// Shared rendering for the C2M canvas — used by both the shell unrolled
// view and the floor top-down view. Centralises the colour scale, legend
// layout, scanline arrow style, and canvas dimensions so the two pages
// stay visually identical.

// ── 4-stop colour gradient (min C2M → blue, max → red) ────────────
export const C2M_COLOR_STOPS = [
  { pct:   0.0,       rgb: [0x00, 0x00, 0xFF] }, // blue
  { pct:  33.3333333, rgb: [0x00, 0xFF, 0x00] }, // green
  { pct:  66.883117,  rgb: [0xFF, 0xFF, 0x00] }, // yellow
  { pct: 100.0,       rgb: [0xFF, 0x00, 0x00] }, // red
];

export function rgbAtPercent(pct) {
  if (!Number.isFinite(pct)) return [0, 255, 0];
  const stops = C2M_COLOR_STOPS;
  if (pct <= stops[0].pct)                return stops[0].rgb;
  if (pct >= stops[stops.length - 1].pct) return stops[stops.length - 1].rgb;
  for (let i = 0; i < stops.length - 1; i++) {
    const a = stops[i], b = stops[i + 1];
    if (pct >= a.pct && pct <= b.pct) {
      const t = (pct - a.pct) / (b.pct - a.pct);
      return [
        Math.round(a.rgb[0] + t * (b.rgb[0] - a.rgb[0])),
        Math.round(a.rgb[1] + t * (b.rgb[1] - a.rgb[1])),
        Math.round(a.rgb[2] + t * (b.rgb[2] - a.rgb[2])),
      ];
    }
  }
  return [0, 0, 0];
}

export function colorStrAtPercent(pct) {
  const [r, g, b] = rgbAtPercent(pct);
  return `rgb(${r},${g},${b})`;
}

export function colorForC2M(c2m, cMin, cMax) {
  const range = cMax - cMin;
  if (!Number.isFinite(range) || range <= 0) return colorStrAtPercent(50);
  return colorStrAtPercent(((c2m - cMin) / range) * 100);
}

// ── Canvas + legend layout (shared) ───────────────────────────────
export const C2M_CANVAS = Object.freeze({
  WIDTH:  3306,
  HEIGHT: 1800,
});

export const C2M_LEGEND = (() => {
  const LABELS_W     = 90;
  const GAP_W        = 10;
  const BAR_W        = 80;
  const GRAPH_W      = 73;
  const RIGHT_MARGIN = 50;
  const AREA_W = LABELS_W + GAP_W + BAR_W + GAP_W + GRAPH_W;     // 263
  const AREA_X = C2M_CANVAS.WIDTH - AREA_W - RIGHT_MARGIN;        // 2993
  return Object.freeze({ LABELS_W, GAP_W, BAR_W, GRAPH_W, RIGHT_MARGIN, AREA_W, AREA_X });
})();

// ── Scanline arrow style (shared) ─────────────────────────────────
export const C2M_ARROW = Object.freeze({
  HEAD_SIZE:   18,   // height of the filled triangle, from base to tip
  HEAD_HALF_B: 9,    // half-base width of the filled triangle
  EXTENSION:   50,   // px the arrow extends past the tank edge
  SHAFT_WIDTH: 3,
  LABEL_GAP:   14,
  LABEL_FONT:  '38px Calibri, "Calibri Body", Arial, Helvetica, sans-serif',
  TITLE_FONT:  '35px Calibri, "Calibri Body", Arial, Helvetica, sans-serif',
});

// ── Legend renderer ────────────────────────────────────────────────
// Draws the legend block on the right edge of the canvas: numeric tick
// labels, the vertical gradient bar, and the colour-filled line-graph
// histogram. Called once per redraw, after the tank cells are painted.
export function drawC2MLegend(ctx, { cMin, cMax, histogram }) {
  const range = cMax - cMin;
  if (range <= 0 || !histogram) return;

  const padTop    = Math.round(C2M_CANVAS.HEIGHT * 0.06);
  const padBottom = Math.round(C2M_CANVAS.HEIGHT * 0.06);
  const legendY0  = padTop;
  const legendH   = C2M_CANVAS.HEIGHT - padTop - padBottom;

  const xLabelsRight = C2M_LEGEND.AREA_X + C2M_LEGEND.LABELS_W;
  const xBar         = xLabelsRight + C2M_LEGEND.GAP_W;
  const xGraphStart  = xBar + C2M_LEGEND.BAR_W + C2M_LEGEND.GAP_W;

  const { counts, NBINS, binWidth, maxCount } = histogram;

  for (let dy = 0; dy < legendH; dy++) {
    const c2m = cMax - (dy / Math.max(1, legendH - 1)) * range;
    const color = colorForC2M(c2m, cMin, cMax);
    ctx.fillStyle = color;
    ctx.fillRect(xBar, legendY0 + dy, C2M_LEGEND.BAR_W, 1);
    const idxF = (c2m - cMin) / binWidth - 0.5;
    let iLo = Math.floor(idxF);
    let iHi = iLo + 1;
    const t = idxF - Math.floor(idxF);
    if (iLo < 0) iLo = 0;
    if (iLo >= NBINS) iLo = NBINS - 1;
    if (iHi < 0) iHi = 0;
    if (iHi >= NBINS) iHi = NBINS - 1;
    const interpCount = counts[iLo] * (1 - t) + counts[iHi] * t;
    const fillW = maxCount > 0 ? (interpCount / maxCount) * C2M_LEGEND.GRAPH_W : 0;
    if (fillW > 0) {
      ctx.fillStyle = color;
      ctx.fillRect(xGraphStart, legendY0 + dy, fillW, 1);
    }
  }

  ctx.fillStyle = '#FFFFFF';
  ctx.font = C2M_ARROW.LABEL_FONT;
  ctx.textAlign = 'right';
  ctx.textBaseline = 'middle';
  const tickSet = new Set([cMin, cMin + range * 0.25, cMin + range * 0.5, cMin + range * 0.75, cMax]);
  if (cMin < 0 && cMax > 0) tickSet.add(0);
  const ticks = Array.from(tickSet).sort((a, b) => a - b);
  for (const v of ticks) {
    const dy = ((cMax - v) / range) * (legendH - 1);
    const y  = legendY0 + dy;
    const txt = v > 0 ? `+${v.toFixed(0)} mm` : `${v.toFixed(0)} mm`;
    ctx.fillRect(xLabelsRight + 4, y - 0.5, C2M_LEGEND.GAP_W - 4, 1);
    ctx.fillText(txt, xLabelsRight, y);
  }

  ctx.font = C2M_ARROW.TITLE_FONT;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'bottom';
  ctx.fillStyle = '#FFFFFF';
  ctx.fillText('C2M (mm)', C2M_LEGEND.AREA_X + C2M_LEGEND.AREA_W / 2, legendY0 - 12);
}

// ── Scanline arrow renderer ────────────────────────────────────────
// Draws a single arrow: shaft from (x1, y1) to (x2, y2) with optional
// arrowheads at one or both ends. The label is anchored relative to
// the chosen "tip" position, offset outward along the shaft direction.
//
//   headEnds : 'end' | 'both' | 'none'
//   labelAt  : 'start' | 'end' | 'both' | null
//
// For the shell vertical scanline, callers use:
//   x1 = tip-top, y1 = tankY − EXTENSION;
//   x2 = tip-bot, y2 = tankY + tankH + EXTENSION;
//   headEnds = 'both', labelAt = 'start' (label above the top tip).
//
// For the floor radial scanline, callers use:
//   x1 = floorCx, y1 = floorCy;
//   x2 = floorCx + (rim_px + EXTENSION) × sin(θ),
//   y2 = floorCy − (rim_px + EXTENSION) × cos(θ);
//   headEnds = 'end', labelAt = 'end'.
export function drawScanlineArrow(ctx, opts) {
  const {
    x1, y1, x2, y2,
    label = null,
    headEnds = 'end',
    labelAt  = 'end',
    labelGap = C2M_ARROW.LABEL_GAP,
    // 'bottom' anchors the text on its baseline (label appears *above*
    // the anchor y) — what the shell wants for north-facing labels.
    // 'middle' anchors the text on its visual centre — better for
    // radial arrows where the label sits to either side of the tip.
    labelBaseline = 'bottom',
  } = opts;
  const dx = x2 - x1;
  const dy = y2 - y1;
  const len = Math.sqrt(dx * dx + dy * dy);
  if (len === 0) return;
  const ux = dx / len, uy = dy / len;
  const perpX = -uy, perpY = ux;
  const H = C2M_ARROW.HEAD_SIZE;
  const B = C2M_ARROW.HEAD_HALF_B;

  // Shrink shaft endpoints inward by HEAD_SIZE on any end that has a head.
  let sx1 = x1, sy1 = y1, sx2 = x2, sy2 = y2;
  if (headEnds === 'both' || headEnds === 'start') {
    sx1 = x1 + ux * H; sy1 = y1 + uy * H;
  }
  if (headEnds === 'both' || headEnds === 'end') {
    sx2 = x2 - ux * H; sy2 = y2 - uy * H;
  }

  ctx.strokeStyle = '#FFFFFF';
  ctx.fillStyle   = '#FFFFFF';
  ctx.lineWidth   = C2M_ARROW.SHAFT_WIDTH;

  // Shaft.
  ctx.beginPath();
  ctx.moveTo(sx1, sy1);
  ctx.lineTo(sx2, sy2);
  ctx.stroke();

  // Filled-triangle arrowheads.
  if (headEnds === 'both' || headEnds === 'end') {
    ctx.beginPath();
    ctx.moveTo(x2, y2);
    ctx.lineTo(sx2 + perpX * B, sy2 + perpY * B);
    ctx.lineTo(sx2 - perpX * B, sy2 - perpY * B);
    ctx.closePath();
    ctx.fill();
  }
  if (headEnds === 'both' || headEnds === 'start') {
    ctx.beginPath();
    ctx.moveTo(x1, y1);
    ctx.lineTo(sx1 + perpX * B, sy1 + perpY * B);
    ctx.lineTo(sx1 - perpX * B, sy1 - perpY * B);
    ctx.closePath();
    ctx.fill();
  }

  // Label (positioned outward of the chosen tip along the shaft direction).
  if (label && labelAt) {
    ctx.font = C2M_ARROW.LABEL_FONT;
    ctx.textAlign = 'center';
    ctx.textBaseline = labelBaseline;
    ctx.fillStyle = '#FFFFFF';
    if (labelAt === 'start' || labelAt === 'both') {
      ctx.fillText(label, x1 - ux * labelGap, y1 - uy * labelGap);
    }
    if (labelAt === 'end' || labelAt === 'both') {
      ctx.fillText(label, x2 + ux * labelGap, y2 + uy * labelGap);
    }
  }
}
